import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { inject, Injector, Input, type OnInit } from '@angular/core';
import { type RailMenuItem } from '@modules/common/components/rail-menu/rail-menu';
import { CanPurgeTeamItemPipe } from '@modules/common/pipes/can-purge-team-item.pipe';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { itemFollowStoreFor } from '@modules/common/services/item-follow/item-follow-store';
import { clearItemFollows } from '@modules/common/services/item-follow/item-follow-messages';
import { PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';
// Common
import { Component } from '@angular/core';

// Utils
import { Like } from '@modules/common/types/like';
import { checkExhaustiveness } from '@modules/common/utils/switch';
import { addHours } from '@sharedlib/common/utils/date';

// Pipes
import { DateByDayIndexPipe } from '@modules/utils/pipes/date-by-day-index.pipe';

// Types
import { Application } from '@modules/common/types/application';
import { Project } from '@modules/tasks/types/project';
import { ProjectsFilters } from '@modules/tasks/types/projects-filters';
import { Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';

// Services
import { ProjectsService } from '@modules/tasks/services/projects.service';
import { TasksService } from '@modules/tasks/services/tasks.service';

// Components
import { BaseSidebarContextMenuComponent } from '@modules/common/components/base-sidebar-context-menu/base-sidebar-context-menu.component';
import { SettingsWindowOpenerService } from '@modules/settings-window/settings-window-opener.service';
import { Priority } from '@sharedlib/common/types/tasking-app/priority';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { TASKS_RAIL_ROWS, type TasksRailKey, type TasksRailScope, tasksRowMenu } from './tasks-rail-menu';
// The one bounded-read helper Notes' rail uses for the same job; moving it to common is a follow-up for both apps.
import { clearRailFollows } from '@modules/notes/components/sidebar-context-menu/rail-clear-follows';

@Component({
  selector: 'app-sidebar-context-menu',
  templateUrl: './sidebar-context-menu.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-context-menu/base-sidebar-context-menu.component.less',
    './sidebar-context-menu.component.less',
  ],
  // Resolved here so the rail's purge refusal asks the very pipe the old "Empty trash" entry asks.
  providers: [CanPurgeTeamItemPipe],
  standalone: false,
})
export class SidebarContextMenuComponent
  extends BaseSidebarContextMenuComponent<StitchType.TASK, StitchType.PROJECT, TasksRailKey>
  implements OnInit
{
  protected readonly followUpWording = injectFollowUpWording();
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;

  advancedSearchStateKey = Application.tasking;

  /** A workspace rail row: the shared rail menu (`tasks-rail-menu.ts`). Other menus keep the template below it. */
  @Input() public rail = false;
  /** Whether the row belongs to the member's My Workspace or to the Team Workspace. */
  @Input() public railScope: TasksRailScope = 'mine';

  /** "New task" / "New project" on a rail row: plain drafts, never carrying the row's marker or the selection. */
  protected readonly railNewTask = new Task();
  protected readonly railNewProject = new Project();
  /**
   * The tenant a rail row's quick forms and bulk requests name: the row's own, never the selection's. Passed as
   * the forms' `tenantItems` rather than stamped on the drafts, so a project a form is given still outranks it.
   */
  protected railTenantItems: ReadonlyArray<{ accountId: string | undefined }> = [];

  // The same instances the base class holds, typed as the concrete services: only they own the follow store.
  private readonly railTasks = inject(TasksService);
  private readonly railProjects = inject(ProjectsService);
  private readonly canPurgeTeamItem = inject(CanPurgeTeamItemPipe);
  private railMenuCache: { key: string; menu: readonly RailMenuItem[] | undefined } | undefined;
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly accountKind = inject(AccountKindService);
  private readonly injector = inject(Injector);
  protected get isTeamRail(): boolean {
    return this.tenantId != null && this.accountKind.isTeam() && this.spaceContextStore.insideTeam();
  }
  private readonly followToaster = inject(ToasterService);
  protected clearMySubscriptions(): void {
    clearItemFollows(itemFollowStoreFor(this.itemsService), this.tenantId, this.followToaster).subscribe();
  }

  private dateByDayIndexPipe = new DateByDayIndexPipe();

  constructor(tasksService: TasksService, projectsService: ProjectsService) {
    super(tasksService, projectsService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.railTenantItems = [{ accountId: this.tenantId ?? undefined }];
  }

  /** Whether this person may empty this row's Trash: the gate of today's "Empty trash". */
  private get purgeAllowed(): boolean {
    return this.canPurgeTeamItem.transform(this.tenantId);
  }

  /** The rail menu of this row, or `undefined` for a row that keeps the template's own menu. */
  protected get railMenu(): readonly RailMenuItem[] | undefined {
    if (!this.rail) return undefined;
    const purgeRefused = this.sidebarFilterKey === 'deleted' && !this.purgeAllowed ? PURGE_REFUSED_REASON : undefined;
    const appSettings = this.accountKind.isTeam();
    const key = `${this.sidebarFilterKey}|${this.railScope}|${purgeRefused ?? ''}|${appSettings}`;
    if (this.railMenuCache?.key !== key) {
      this.railMenuCache = {
        key,
        menu: tasksRowMenu(this.sidebarFilterKey, this.railScope, { purgeRefused, appSettings }),
      };
    }
    return this.railMenuCache.menu;
  }

  /** "View settings…" on this row: the card for the list the row opens. */
  protected openRowViewSettings(): void {
    this.openViewSettings(this.sidebarFilterKey === 'all_projects' ? 'projects' : 'tasks');
  }

  protected runRailEntry(id: string): void {
    const entry = this.railMenu?.find((candidate) => candidate?.id === id);
    // The shared renderer already answers Coming soon and refuses a disabled entry; a direct call must too.
    if (!entry || entry.comingSoon || entry.disabled) return;
    switch (id) {
      case 'settings':
        this.openRowViewSettings();
        return;
      case 'app-settings':
        // The row's workspace, never the one on screen: a Team Workspace row opens Team apps › Tasks.
        this.injector.get(SettingsWindowOpenerService).openPage([this.railScope === 'team' ? 'team' : 'my', 'tasks']);
        return;
      case 'clear':
        if (this.sidebarFilterKey === 'following') void this.clearRailSubscriptions();
        else this.clearRailRow();
        return;
      case 'clear-team':
        this.clearRailRow();
        return;
      case 'empty-trash-all':
        if (this.purgeAllowed) this.emptyTrash();
        return;
    }
  }

  /**
   * Team Following · Clear my subscriptions. The whole-list DELETE would also clear follows of archived and
   * trashed items the view never shows, so this reads what the view lists first and unfollows those, one by one.
   */
  private async clearRailSubscriptions(): Promise<void> {
    const tenant = this.tenantId;
    if (this.railScope !== 'team' || !tenant || tenant !== this.spaceContextStore.contextId()) return;
    const tasks = this.railTasks;
    const projects = this.railProjects;
    await clearRailFollows(
      tenant,
      this.spaceContextStore,
      [
        {
          list: (offset, limit) => tasks.follow.api.list(tenant, offset, limit),
          unfollow: (id) => tasks.follow.api.unfollow(id, tenant),
          record: (id) => tasks.follow.record(id, tenant, false, false),
          refresh: () => tasks.forceRefresh(),
        },
        {
          list: (offset, limit) => projects.follow.api.list(tenant, offset, limit),
          unfollow: (id) => projects.follow.api.unfollow(id, tenant),
          record: (id) => projects.follow.record(id, tenant, false, false),
          refresh: () => projects.forceRefresh(),
        },
      ],
      this.followToaster,
    );
  }

  /** Clears only the clicked row's visible tasks and projects in the row's tenant, as of one fixed instant. */
  private clearRailRow(): void {
    const key = this.sidebarFilterKey;
    if (key !== 'flagged' && key !== 'followed' && key !== 'snoozed') return;
    const cutoff = new Date();
    const tasks = new TasksFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const projects = new ProjectsFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const taskConfig = { tenantItems: this.railTenantItems };
    const projectConfig = { tenantItems: this.railTenantItems };
    if (key === 'flagged') {
      this.itemsService.flag(tasks, false, taskConfig);
      this.containerItemsService.flag(projects, false, projectConfig);
    } else if (key === 'followed') {
      this.itemsService.followUp(tasks, null, taskConfig);
      this.containerItemsService.followUp(projects, null, projectConfig);
    } else {
      this.itemsService.snooze(tasks, null, taskConfig);
      this.containerItemsService.snooze(projects, null, projectConfig);
    }
  }

  protected getCommonParams(): Like<Task | Project> {
    // A rail row the shared menu covers acts on THAT ROW's tenant with a plain draft, whatever the search has
    // selected. The rows it does not cover (Next 7 days, Priority and their children) keep the legacy defaults below.
    if (this.rail && TASKS_RAIL_ROWS[this.sidebarFilterKey]) return { accountId: this.tenantId };

    let params: Like<Task | Project> = {};
    let date = new Date();

    switch (this.sidebarFilterKey) {
      case 'following':
      case 'assigned_to_me':
      case 'shared':
      case 'search_results':
      case 'all_tasks':
      case 'all_projects':
        break;
      case 'overdue':
        date.setHours(9, 0, 0, 0);

        params.endDateTime = date;
        break;
      case 'today':
        date = addHours(date, 1);
        params.startDateTime = date;
        break;
      case 'day0':
      case 'day1':
      case 'day2':
      case 'day3':
      case 'day4':
      case 'day5':
      case 'day6':
      case 'week':
        date = this.dateByDayIndexPipe.transform(this.sidebarFilterKey);
        date.setHours(9, 0, 0, 0);

        params.startDateTime = date;
        break;
      case 'priority':
        params.priority = Priority.High;
        break;
      case 'low':
      case 'normal':
      case 'high':
        params.priority = this.sidebarFilterKey;
        break;
      case 'snoozed':
      case 'followed':
      case 'flagged':
      case 'deleted':
      case 'archived':
        params = super.getCommonParams();
        break;
      default:
        checkExhaustiveness(this.sidebarFilterKey);
    }

    return params;
  }

  protected stitchItemFactory(params: Like<Task>): Task {
    return new Task({ ...params, projectId: this.selectedContainerStitchItemId });
  }

  protected containerStitchItemFactory(params: Like<Project>): Project {
    return new Project({ ...params, parentId: this.selectedContainerStitchItemId });
  }
}
